import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: ReactNode;
  /** Rendered under the field; replaced by `error` when one is present. */
  hint?: ReactNode;
  /** Non-empty means invalid: sets aria-invalid and wires aria-describedby. */
  error?: string | undefined;
  leadingAddon?: ReactNode;
  trailingAddon?: ReactNode;
  containerClassName?: string;
  /**
   * `on-gradient` paints the label, hint and error in inks that survive the
   * brand gradient. The default greys (`--text-secondary` / `--text-muted`) are
   * dark and land at roughly 2.5:1 on the blue — legible on paper, not to
   * anyone who needs the contrast.
   *
   * `on-elevated` is for a field inside a Modal or BottomSheet. The default
   * field background is `--surface`, and on an elevated surface that is the
   * SAME COLOUR (`#ffffff` against `#ffffff` in light) — so the field becomes
   * invisible and the only thing marking it is its placeholder. This swaps the
   * fill to `--surface-sunken`, which is what gives the input an edge on a
   * white dialog. The inks are unchanged; only the fill differs.
   */
  tone?: 'default' | 'on-gradient' | 'on-elevated';
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    hint,
    error,
    leadingAddon,
    trailingAddon,
    id,
    className,
    containerClassName,
    disabled,
    required,
    tone = 'default',
    ...rest
  },
  ref
) {
  const onGradient = tone === 'on-gradient';
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const messageId = `${inputId}-message`;
  const hasError = Boolean(error);
  const message = error ?? hint;

  return (
    <div className={cn('flex w-full flex-col gap-1.5', containerClassName)}>
      {label !== undefined && (
        <label
          htmlFor={inputId}
          className={cn(
            onGradient
              ? 'text-body font-semibold text-[var(--on-gradient)]'
              : 'text-body-sm font-medium text-[var(--text-secondary)]'
          )}
        >
          {label}
          {required === true && (
            <span aria-hidden className="ml-0.5 text-[var(--danger-text)]">
              *
            </span>
          )}
        </label>
      )}

      {/*
        Borderless white field on a 12px radius, per Log_in_2 and every form in
        the mockup: the surface itself is the affordance, not a 1px outline. The
        focus ring is drawn as an outline rather than a border swap so the field
        does not change size by a pixel when focused.
      */}
      <div
        className={cn(
          'flex items-center gap-2 rounded-md px-4',
          tone === 'on-elevated' ? 'bg-[var(--surface-sunken)]' : 'bg-[var(--surface)]',
          'transition-[outline-color] duration-150',
          'focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-[var(--focus-ring)]',
          hasError && 'outline-2 outline-[var(--danger)]',
          disabled === true && 'cursor-not-allowed opacity-60',
          containerClassName
        )}
      >
        {leadingAddon !== undefined && (
          <span className="shrink-0 text-[var(--text-muted)]">{leadingAddon}</span>
        )}
        <input
          ref={ref}
          id={inputId}
          disabled={disabled}
          required={required}
          aria-invalid={hasError || undefined}
          aria-describedby={message !== undefined ? messageId : undefined}
          className={cn(
            'h-13 w-full bg-transparent text-body text-[var(--text-primary)]',
            'placeholder:text-[var(--text-disabled)]',
            // The container owns the focus ring; a second one on the input
            // itself would double-draw.
            'outline-none disabled:cursor-not-allowed',
            className
          )}
          {...rest}
        />
        {trailingAddon !== undefined && <span className="shrink-0">{trailingAddon}</span>}
      </div>

      {message !== undefined && (
        <p
          id={messageId}
          // Errors are announced; plain hints are not, to avoid chatter.
          role={hasError ? 'alert' : undefined}
          className={cn(
            'text-caption',
            onGradient
              ? hasError
                ? 'self-start rounded-sm bg-[var(--danger)] px-2 py-1 font-semibold text-white'
                : 'text-body-sm text-[var(--on-gradient)]'
              : hasError
                ? 'text-[var(--danger-text)]'
                : 'text-[var(--text-muted)]'
          )}
        >
          {message}
        </p>
      )}
    </div>
  );
});
